import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';
import { getEmployees } from '../api';
import { weekRange } from '../utils';

// Same quick ranges as the Reports page.
const QUICK_RANGES = [
  { label: 'This Week', fn: () => { const { from, to } = weekRange(); return { from: from.split('T')[0], to: to.split('T')[0] }; } },
  { label: 'Last Week', fn: () => {
    const now = new Date(); const day = now.getDay();
    const mon = new Date(now); mon.setDate(now.getDate() - day - 6); mon.setHours(0, 0, 0, 0);
    const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
    return { from: mon.toISOString().split('T')[0], to: sun.toISOString().split('T')[0] };
  } },
  { label: 'This Month', fn: () => {
    const now = new Date();
    return { from: new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0], to: new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0] };
  } },
  { label: 'Last Month', fn: () => {
    const now = new Date();
    return { from: new Date(now.getFullYear(), now.getMonth() - 1, 1).toISOString().split('T')[0], to: new Date(now.getFullYear(), now.getMonth(), 0).toISOString().split('T')[0] };
  } },
];

const fmtHM = (sec) => { sec = Math.max(0, sec || 0); const h = Math.floor(sec / 3600), m = Math.floor((sec % 3600) / 60); return h ? `${h}h ${m}m` : `${m}m`; };
const fmtNum = (n) => { n = n || 0; return n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n); };
const CAT = { productive: { label: 'Productive', color: '#059669' }, unproductive: { label: 'Unproductive', color: '#dc2626' }, neutral: { label: 'Neutral', color: 'var(--muted)' } };
const card = { background: 'var(--glass)', backdropFilter: 'var(--blur)', WebkitBackdropFilter: 'var(--blur)', border: '1px solid var(--glass-border)', borderRadius: 12, padding: 18 };

export default function EmployeeUsage() {
  const { from: wFrom, to: wTo } = weekRange();
  const [employees, setEmployees] = useState([]);
  const [emp, setEmp] = useState('');
  const [from, setFrom] = useState(wFrom.split('T')[0]);
  const [to, setTo] = useState(wTo.split('T')[0]);
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getEmployees().then(es => {
      const list = (es || []).filter(e => e.active !== 0);
      setEmployees(list);
      if (list.length) setEmp(list[0].id);
    }).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    if (!emp) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/app-usage/apps?employee_id=${emp}&from=${from}T00:00:00&to=${to}T23:59:59`);
      setApps(data);
    } catch { setApps([]); }
    setLoading(false);
  }, [emp, from, to]);
  useEffect(() => { load(); }, [load]);

  const totalSecs = apps.reduce((a, x) => a + x.total_seconds, 0);
  const byCat = apps.reduce((acc, x) => { acc[x.category] = (acc[x.category] || 0) + x.total_seconds; return acc; }, {});
  const pct = (s) => totalSecs ? Math.round((s / totalSecs) * 100) : 0;
  const empName = employees.find(e => e.id === emp)?.name || '';

  return (
    <div style={{ padding: 24, maxWidth: 1000, margin: '0 auto' }}>
      <h1 style={{ fontSize: 24, fontWeight: 700, color: 'var(--text)', margin: '0 0 4px' }}>Employee Apps</h1>
      <p style={{ color: 'var(--muted)', fontSize: 14, margin: '0 0 18px' }}>See which applications each employee spends time in. Classify apps on the <strong>App Productivity</strong> page.</p>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
        {QUICK_RANGES.map(r => {
          const rv = r.fn();
          const active = rv.from === from && rv.to === to;
          return (
            <button key={r.label} onClick={() => { setFrom(rv.from); setTo(rv.to); }}
              style={{ padding: '7px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                border: `1px solid ${active ? 'var(--primary)' : 'var(--glass-border)'}`,
                background: active ? 'var(--primary)' : 'var(--glass)', color: active ? '#fff' : 'var(--text-2)' }}>
              {r.label}
            </button>
          );
        })}
      </div>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 18 }}>
        <select value={emp} onChange={e => setEmp(e.target.value)} style={{ padding: '9px 12px', border: '1px solid var(--glass-border)', borderRadius: 8, fontSize: 14, minWidth: 220 }}>
          {employees.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
        </select>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 11, color: 'var(--muted)' }}>From
          <input type="date" value={from} max={to} onChange={e => setFrom(e.target.value)} style={{ padding: '8px 10px', border: '1px solid var(--glass-border)', borderRadius: 8, fontSize: 14 }} />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 11, color: 'var(--muted)' }}>To
          <input type="date" value={to} min={from} onChange={e => setTo(e.target.value)} style={{ padding: '8px 10px', border: '1px solid var(--glass-border)', borderRadius: 8, fontSize: 14 }} />
        </label>
      </div>

      {/* Summary */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
        <div style={{ ...card, flex: 1, minWidth: 150 }}><div style={{ fontSize: 22, fontWeight: 800, color: 'var(--text)' }}>{fmtHM(totalSecs)}</div><div style={{ fontSize: 12, color: 'var(--muted)' }}>Total tracked</div></div>
        {['productive', 'neutral', 'unproductive'].map(c => (
          <div key={c} style={{ ...card, flex: 1, minWidth: 150 }}>
            <div style={{ fontSize: 22, fontWeight: 800, color: CAT[c].color }}>{fmtHM(byCat[c] || 0)}</div>
            <div style={{ fontSize: 12, color: 'var(--muted)' }}>{CAT[c].label} ({pct(byCat[c] || 0)}%)</div>
          </div>
        ))}
      </div>

      <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead><tr style={{ background: 'var(--hairline)' }}>
            <th style={th}>Application / Page</th><th style={th}>Time</th>
            <th style={{ ...th, textAlign: 'right' }}>⌨️ Keys</th><th style={{ ...th, textAlign: 'right' }}>🖱️ Clicks</th>
            <th style={th}>Share</th><th style={{ ...th, textAlign: 'right' }}>Category</th>
          </tr></thead>
          <tbody>
            {apps.map((a, i) => (
              <tr key={`${a.app_name}|${a.title || ''}|${i}`} style={{ borderBottom: '1px solid var(--hairline)' }}>
                <td style={td}>
                  <div style={{ fontWeight: 600, color: 'var(--text)' }}>{a.app_name}</div>
                  {a.title && <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 1, maxWidth: 340, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.title}</div>}
                </td>
                <td style={{ ...td, color: 'var(--text-2)' }}>{fmtHM(a.total_seconds)}</td>
                <td style={{ ...td, textAlign: 'right', color: 'var(--text-2)' }}>{fmtNum(a.keys)}</td>
                <td style={{ ...td, textAlign: 'right', color: 'var(--text-2)' }}>{fmtNum(a.clicks)}</td>
                <td style={td}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ flex: 1, maxWidth: 120, height: 6, background: 'var(--hairline)', borderRadius: 3, overflow: 'hidden' }}>
                      <div style={{ width: `${pct(a.total_seconds)}%`, height: '100%', background: CAT[a.category].color }} />
                    </div>
                    <span style={{ color: 'var(--muted)', fontSize: 12 }}>{pct(a.total_seconds)}%</span>
                  </div>
                </td>
                <td style={{ ...td, textAlign: 'right' }}>
                  <span style={{ background: CAT[a.category].color + '20', color: CAT[a.category].color, padding: '2px 9px', borderRadius: 12, fontSize: 11, fontWeight: 600 }}>{CAT[a.category].label}</span>
                </td>
              </tr>
            ))}
            {!loading && apps.length === 0 && <tr><td colSpan={6} style={{ textAlign: 'center', color: 'var(--muted)', padding: 36 }}>No app activity for {empName} in this period.</td></tr>}
            {loading && <tr><td colSpan={6} style={{ textAlign: 'center', color: 'var(--muted)', padding: 28 }}>Loading…</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const th = { padding: '11px 14px', textAlign: 'left', fontWeight: 600, color: 'var(--text-2)', borderBottom: '1px solid var(--glass-border)' };
const td = { padding: '11px 14px' };
